import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { Modal, message, DatePicker } from "lib/ui";
import {
  Minus,
  Plus,
  ShoppingCart,
  Trash2,
  UserPlus,
  Search,
  StickyNote,
  CalendarClock,
  Receipt,
  Send,
  X,
  Phone,
  Mail,
  User as UserIcon,
  Check,
  Package,
  MapPin,
  CreditCard,
} from "lucide-react";
import { AuthContext } from "../../../context/Context";
import ApiPath from "../../../hooks/ApiFetch";
import { cn } from "../../../lib/cn";
import moment from "moment";

const LaundryApp = () => {
  const [customerName, setCustomerName] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [paid, setPaid] = useState(false);
  const [selectedArray, setSelectedArray] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [pickTime, setPickTime] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [note, setNote] = useState("");
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCust, setNewCust] = useState({ firstname: "", lastname: "", email: "", mobileNumber: "" });
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [confirmOrderOpen, setConfirmOrderOpen] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [pendingOrderPayload, setPendingOrderPayload] = useState(null);
  const dropdownRef = useRef(null);

  const { Users = [], setUser, ProductsTwo, fetchProduct } = useContext(AuthContext);

  useEffect(() => { fetchProduct(); }, []);
  useEffect(() => {
    setFilteredCustomers(Users.map((c) => ({ value: c._id, label: c.name })));
  }, [Users]);
  useEffect(() => {
    if (ProductsTwo?.length && !selectedArray) setSelectedArray(ProductsTwo[0].name);
  }, [ProductsTwo, selectedArray]);
  useEffect(() => {
    const close = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selectedData = useMemo(() => ProductsTwo?.find((i) => i.name === selectedArray), [ProductsTwo, selectedArray]);
  const categories = useMemo(() => (selectedData ? Object.keys(selectedData.categories) : []), [selectedData]);
  const displayedItems = useMemo(() => selectedData?.categories?.[selectedCategory] || [], [selectedData, selectedCategory]);
  const arrayNames = useMemo(() => ProductsTwo?.map((i) => i.name) || [], [ProductsTwo]);
  const selectedCustomer = useMemo(
    () => Users.find((u) => u._id === selectedCustomerId),
    [Users, selectedCustomerId]
  );

  useEffect(() => {
    if (categories.length && !categories.includes(selectedCategory)) setSelectedCategory(categories[0]);
  }, [categories, selectedCategory]);

  const addItem = (item) => {
    const existing = selectedItems.find((s) => s.id === item.id);
    if (existing) {
      setSelectedItems(selectedItems.map((s) => s.id === item.id ? { ...s, quantity: s.quantity + 1, cost: (s.quantity + 1) * s.price } : s));
    } else {
      setSelectedItems([...selectedItems, { categoryName: selectedArray, id: item.id, price: item.price, product: item.product, image: item.image, quantity: 1, cost: item.price }]);
    }
  };

  const updateQty = (id, delta) => {
    setSelectedItems(selectedItems.map((s) => s.id === id ? { ...s, quantity: s.quantity + delta, cost: (s.quantity + delta) * s.price } : s).filter((s) => s.quantity > 0));
  };

  const setQtyDirect = (id, qty) => {
    if (qty <= 0) setSelectedItems(selectedItems.filter((s) => s.id !== id));
    else setSelectedItems(selectedItems.map((s) => s.id === id ? { ...s, quantity: qty, cost: qty * s.price } : s));
  };

  const removeItem = (id) => setSelectedItems(selectedItems.filter((s) => s.id !== id));
  const clearCart = () => setSelectedItems([]);

  const handleCustomerSelect = (id) => {
    const c = Users.find((u) => u._id === id);
    if (c) { setCustomerName(c.name); setSelectedCustomerId(c._id); setShowDropdown(false); }
  };
  const handleCustomerSearch = (text) => {
    setCustomerName(text); setSelectedCustomerId(null);
    setFilteredCustomers(Users.filter((c) => c.name.toLowerCase().includes(text.toLowerCase())).map((c) => ({ value: c._id, label: c.name })));
    setShowDropdown(true);
  };

  const handleAddCustomer = async () => {
    const { firstname, lastname, email, mobileNumber } = newCust;
    if (!firstname.trim() || !lastname.trim()) return message.error("First and last name required.");
    if (!email.trim()) return message.error("Email required.");
    if (!mobileNumber.trim()) return message.error("Mobile number required.");
    setAddingCustomer(true);
    try {
      const response = await ApiPath.post("/users", { ...newCust, name: `${firstname} ${lastname}` });
      setUser((prev) => [...prev, { ...newCust, _id: response.data._id }]);
      setSelectedCustomerId(response?.data?._id);
      setCustomerName(response?.data?.name);
      message.success("Customer added!");
      setShowAddCustomer(false);
      setNewCust({ firstname: "", lastname: "", email: "", mobileNumber: "" });
    } catch { message.error("Failed to add customer."); }
    setAddingCustomer(false);
  };

  const totalQty = selectedItems.reduce((s, i) => s + i.quantity, 0);
  const totalPcs = selectedItems.length;
  const subtotal = selectedItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const discountAmt = subtotal * (discountPercent / 100);
  const tax = 0;
  const totalPrice = subtotal - discountAmt + tax;

  const createOrder = async (d) => (await ApiPath.post("/orders", d)).data;

  const handleSubmitOrder = () => {
    if (!selectedCustomerId) return message.error("Please select a customer.");
    if (selectedItems.length === 0) return message.error("Add items first.");
    if (!pickTime) return message.error("Select a pick-up time.");
    if (!deliveryTime) return message.error("Select a delivery time.");

    const orderItems = selectedItems.reduce((acc, item) => {
      acc[item.id] = {
        categoryName: item.categoryName,
        id: item.id,
        price: item.price,
        product: item.product,
        image: item.image,
        quantity: item.quantity,
      };
      return acc;
    }, {});

    const orderData = {
      status: "completed",
      ref: Math.floor(Math.random() * 1000000),
      deliveryDate: deliveryTime.toISOString(),
      deliveryAddress: {
        latitude: "24.466667",
        longitude: "54.366669",
        address: "Abu Dhabi, Electra Street",
      },
      deliveryFee: 0,
      discount: discountAmt,
      items: orderItems,
      stage: [{ title: "Order Placed", date: new Date().toISOString(), completed: true }],
      pickTime: pickTime.toISOString(),
      subtotal,
      totalPrice,
      balance: paid ? 0 : totalPrice,
      paymentmode: "cash",
      userId: selectedCustomerId,
      note,
    };

    setPendingOrderPayload(orderData);
    setConfirmOrderOpen(true);
  };

  const resetOrderForm = () => {
    setSelectedItems([]);
    setCustomerName("");
    setSelectedCustomerId(null);
    setPaid(false);
    setPickTime(null);
    setDeliveryTime(null);
    setDiscountPercent(0);
    setNote("");
    setPendingOrderPayload(null);
  };

  const submitConfirmedOrder = async () => {
    if (!pendingOrderPayload) return;
    setSubmittingOrder(true);
    try {
      await createOrder(pendingOrderPayload);
      message.success("Order submitted!");
      setConfirmOrderOpen(false);
      resetOrderForm();
    } catch {
      message.error("Failed to submit order.");
    }
    setSubmittingOrder(false);
  };

  return (
    <div className="flex h-[78vh] gap-5 p-1">

      {/* ═══════════ LEFT — PRODUCT PICKER ═══════════ */}
      <div className="flex min-w-0 flex-[3] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        {/* service tabs */}
        <div className="flex gap-1 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50 px-3 pt-1">
          {arrayNames.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setSelectedArray(name)}
              className={cn(
                "relative rounded-t-xl px-5 py-3 text-sm font-bold capitalize transition",
                selectedArray === name
                  ? "bg-white text-brand shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              )}
            >
              {name}
              {selectedArray === name && (
                <span className="absolute inset-x-3 bottom-0 h-[3px] rounded-full bg-brand" />
              )}
            </button>
          ))}
        </div>

        {/* category pills */}
        <div className="flex items-center gap-2 overflow-x-auto px-5 py-3">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={cn(
                "shrink-0 rounded-lg px-4 py-2 text-sm font-semibold transition",
                selectedCategory === cat
                  ? "bg-brand text-white shadow"
                  : "bg-slate-100 text-slate-600 hover:bg-brand-surface hover:text-brand-dark"
              )}
            >
              {cat || "Other"}
            </button>
          ))}
        </div>

        {/* product grid */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {displayedItems.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-300">
              <ShoppingCart className="h-14 w-14" strokeWidth={1} />
              <p className="text-base">No products in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {displayedItems.map((item) => {
                const inCart = selectedItems.find((s) => s.id === item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => addItem(item)}
                    className={cn(
                      "group relative flex flex-col items-center gap-2 rounded-2xl border-2 bg-white px-3 pb-3 pt-4 transition hover:shadow-lg",
                      inCart
                        ? "border-brand bg-brand-surface/40 shadow-md"
                        : "border-transparent bg-slate-50/80 hover:border-brand/30"
                    )}
                  >
                    <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-white">
                      <img
                        src={item.image}
                        alt={item.product}
                        className="max-h-16 max-w-16 object-contain drop-shadow"
                        loading="lazy"
                        onError={(e) => { e.target.src = "/products/other.svg"; }}
                      />
                    </div>
                    <span className="w-full truncate text-center text-sm font-semibold capitalize text-slate-700">
                      {item.product}
                    </span>
                    {inCart && (
                      <span className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-brand text-xs font-bold text-white shadow-lg">
                        {inCart.quantity}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ RIGHT — ORDER PANEL ═══════════ */}
      <div className="flex w-[380px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm xl:w-[420px]">

        {/* customer bar */}
        <div className="border-b border-slate-100 px-5 py-3">
          {!showAddCustomer ? (
            <>
              <div className="relative flex items-center gap-2" ref={dropdownRef}>
                <Search className="pointer-events-none absolute left-3 h-4 w-4 text-slate-400" />
                <input
                  placeholder="Search customer..."
                  value={customerName}
                  onChange={(e) => handleCustomerSearch(e.target.value)}
                  onFocus={() => setShowDropdown(true)}
                  className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/20"
                />
                <button
                  type="button"
                  onClick={() => setShowAddCustomer(true)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand text-white shadow transition hover:bg-brand-dark"
                >
                  <UserPlus className="h-4 w-4" />
                </button>
                {showDropdown && filteredCustomers.length > 0 && (
                  <ul className="absolute left-0 right-12 top-full z-50 mt-1 max-h-48 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl [&::-webkit-scrollbar]:hidden">
                    {filteredCustomers.map((c) => (
                      <li key={c.value}>
                        <button
                          type="button"
                          className="flex w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-brand-surface hover:text-brand-dark"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleCustomerSelect(c.value)}
                        >
                          {c.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {selectedCustomerId && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-brand-surface/60 px-3 py-2">
                  <UserIcon className="h-4 w-4 text-brand" />
                  <span className="text-sm font-semibold text-brand-dark">{customerName}</span>
                  <button type="button" onClick={() => { setCustomerName(""); setSelectedCustomerId(null); }} className="ml-auto text-slate-400 hover:text-red-500">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-slate-800">Quick Add Customer</h4>
                <button type="button" onClick={() => setShowAddCustomer(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <UserIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                  <input placeholder="First name" value={newCust.firstname} onChange={(e) => setNewCust({ ...newCust, firstname: e.target.value })}
                    className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand focus:ring-1 focus:ring-brand/20" />
                </div>
                <input placeholder="Last name" value={newCust.lastname} onChange={(e) => setNewCust({ ...newCust, lastname: e.target.value })}
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand focus:ring-1 focus:ring-brand/20" />
              </div>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input placeholder="Email" type="email" value={newCust.email} onChange={(e) => setNewCust({ ...newCust, email: e.target.value })}
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand focus:ring-1 focus:ring-brand/20" />
              </div>
              <div className="relative">
                <Phone className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input placeholder="Mobile number" value={newCust.mobileNumber} onChange={(e) => setNewCust({ ...newCust, mobileNumber: e.target.value })}
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand focus:ring-1 focus:ring-brand/20" />
              </div>
              <button
                type="button"
                onClick={handleAddCustomer}
                disabled={addingCustomer}
                className="flex h-9 w-full items-center justify-center gap-2 rounded-xl bg-brand text-sm font-bold text-white shadow transition hover:bg-brand-dark disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                {addingCustomer ? "Adding..." : "Add Customer"}
              </button>
            </div>
          )}
        </div>

        {/* items list */}
        <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {selectedItems.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100">
                <Receipt className="h-8 w-8 text-slate-300" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-400">No items yet</p>
                <p className="mt-1 text-xs text-slate-300">Select products from the left</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {selectedItems.map((item) => (
                <div key={item.id} className="group flex items-center gap-3 px-5 py-3 transition hover:bg-slate-50/80">
                  {/* product name */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold capitalize text-slate-800">{item.product}</p>
                    <p className="text-xs text-slate-400">AED {item.price} each</p>
                  </div>
                  {/* qty controls */}
                  <div className="flex items-center gap-1 rounded-xl bg-slate-100 p-1">
                    <button
                      type="button"
                      onClick={() => updateQty(item.id, -1)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-white hover:text-red-500 hover:shadow-sm"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => setQtyDirect(item.id, parseInt(e.target.value) || 0)}
                      className="h-8 w-10 bg-transparent text-center text-sm font-bold text-slate-800 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => updateQty(item.id, 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-white hover:text-brand hover:shadow-sm"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  {/* price */}
                  <span className="w-20 text-right text-sm font-bold tabular-nums text-slate-900">
                    {(item.price * item.quantity).toFixed(2)}
                  </span>
                  {/* remove */}
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-300 opacity-0 transition group-hover:opacity-100 hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── bottom section ── */}
        <div className="border-t border-slate-200 bg-slate-50/50">
          {/* summary + clear */}
          <div className="flex items-center gap-2 px-5 py-2.5">
            <span className="rounded-lg bg-brand-surface px-3 py-1.5 text-xs font-bold text-brand">
              {totalPcs} item{totalPcs !== 1 ? "s" : ""}
            </span>
            <span className="text-sm text-slate-400">{totalQty} qty</span>
            <div className="flex-1" />
            {selectedItems.length > 0 && (
              <button type="button" onClick={clearCart} className="text-xs font-semibold text-red-400 hover:text-red-600">Clear all</button>
            )}
          </div>

          {/* schedule — always visible */}
          <div className="border-t border-slate-100 px-5 py-3">
            <div className="mb-2 flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-brand" />
              <span className="text-sm font-semibold text-slate-700">Schedule</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">Pick-up</label>
                <DatePicker
                  showTime
                  placeholder="Select date & time"
                  value={pickTime}
                  onChange={setPickTime}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">Delivery</label>
                <DatePicker
                  showTime
                  placeholder="Select date & time"
                  value={deliveryTime}
                  onChange={setDeliveryTime}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>

          {/* note */}
          <div className="border-t border-slate-100 px-5 py-3">
            <div className="flex items-center gap-2">
              <StickyNote className="h-3.5 w-3.5 text-slate-400" />
              <input
                placeholder="Add a note for this order..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
              />
            </div>
          </div>

          {/* pricing */}
          <div className="space-y-2 border-t border-slate-100 px-5 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">Subtotal</span>
              <span className="text-sm font-semibold tabular-nums text-slate-700">{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Discount</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercent || ""}
                  onChange={(e) => setDiscountPercent(Math.min(100, parseInt(e.target.value) || 0))}
                  placeholder="%"
                  className="h-7 w-14 rounded-lg border border-slate-200 bg-white text-center text-xs font-semibold text-slate-600 outline-none focus:border-brand [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
              </div>
              <span className={cn("text-sm font-semibold tabular-nums", discountAmt > 0 ? "text-red-500" : "text-slate-400")}>
                {discountAmt > 0 ? `-${discountAmt.toFixed(2)}` : "0.00"}
              </span>
            </div>
            {tax > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Tax</span>
                <span className="text-sm font-semibold tabular-nums text-slate-700">{tax.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* total + submit */}
          <div className="flex items-center gap-3 border-t border-slate-200 bg-white px-5 py-4">
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-extrabold tabular-nums tracking-tight text-slate-900">
                  AED {totalPrice.toFixed(2)}
                </span>
                <button
                  type="button"
                  onClick={() => setPaid(!paid)}
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-[11px] font-bold transition",
                    paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  )}
                >
                  {paid ? "Paid" : "Unpaid"}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSubmitOrder}
              disabled={selectedItems.length === 0}
              className="flex h-12 items-center gap-2 rounded-xl bg-brand px-6 text-sm font-bold text-white shadow-lg transition hover:bg-brand-dark disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
            >
              Submit
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <Modal
        visible={confirmOrderOpen}
        onCancel={() => {
          if (submittingOrder) return;
          setConfirmOrderOpen(false);
          setPendingOrderPayload(null);
        }}
        title="Review & confirm order"
        okText="Place order"
        cancelText="Go back"
        confirmLoading={submittingOrder}
        onOk={submitConfirmedOrder}
        width={560}
        className="max-w-xl overflow-hidden"
      >
        {pendingOrderPayload && (
          <div className="space-y-4">
            <div className="-mt-1 flex items-center gap-3 rounded-xl bg-brand-surface/50 px-3 py-2.5 ring-1 ring-brand/15">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-brand/10">
                <Receipt className="h-5 w-5 text-brand" />
              </span>
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-slate-800">
                  Reference <span className="tabular-nums text-brand-dark">#{pendingOrderPayload.ref}</span>
                </p>
                <p className="text-xs text-slate-500">
                  {selectedItems.length} line{selectedItems.length !== 1 ? "s" : ""} · {totalQty} pieces · Cash
                </p>
              </div>
            </div>
            {/* Customer */}
            <div className="rounded-xl border border-violet-200/60 bg-gradient-to-br from-brand-surface/40 to-white p-4 ring-1 ring-brand/10">
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-brand-dark">
                <UserIcon className="h-3.5 w-3.5" />
                Customer
              </div>
              <p className="text-base font-bold text-slate-900">{customerName}</p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                {selectedCustomer?.email ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    {selectedCustomer.email}
                  </span>
                ) : null}
                {selectedCustomer?.mobileNumber ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    {selectedCustomer.mobileNumber}
                  </span>
                ) : null}
              </div>
            </div>

            {/* Schedule & delivery */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <CalendarClock className="h-3.5 w-3.5 text-brand" />
                  Pick-up
                </div>
                <p className="text-sm font-semibold text-slate-800">
                  {pickTime ? moment(pickTime).format("ddd, MMM D · h:mm A") : "—"}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <CalendarClock className="h-3.5 w-3.5 text-brand" />
                  Delivery
                </div>
                <p className="text-sm font-semibold text-slate-800">
                  {deliveryTime ? moment(deliveryTime).format("ddd, MMM D · h:mm A") : "—"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <span>{pendingOrderPayload.deliveryAddress?.address}</span>
            </div>

            {/* Line items */}
            <div>
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                <Package className="h-3.5 w-3.5 text-brand" />
                Items
              </div>
              <div className="max-h-[220px] overflow-y-auto rounded-xl border border-slate-200 bg-white [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-right">Each</th>
                      <th className="px-3 py-2 text-right">Line</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {selectedItems.map((item) => (
                      <tr key={item.id} className="text-slate-800">
                        <td className="px-3 py-2.5 font-medium capitalize">{item.product}</td>
                        <td className="px-3 py-2.5 text-center tabular-nums font-semibold">{item.quantity}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{item.price.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-bold">
                          {(item.price * item.quantity).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {note.trim() ? (
              <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 px-3 py-2.5">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-bold text-amber-800">
                  <StickyNote className="h-3.5 w-3.5" />
                  Order note
                </div>
                <p className="text-sm text-amber-950/90">{note.trim()}</p>
              </div>
            ) : null}

            {/* Payment summary */}
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <CreditCard className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-700">
                {paid ? "Paid in full" : "Payment due on fulfillment"}
              </span>
              <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-bold text-slate-600 ring-1 ring-slate-200">
                Cash
              </span>
              {!paid && (
                <span className="text-xs font-medium text-amber-700">
                  Balance: AED {totalPrice.toFixed(2)}
                </span>
              )}
            </div>

            {/* Totals */}
            <div className="space-y-2 rounded-xl border border-brand/15 bg-gradient-to-b from-white to-brand-surface/30 p-4 ring-1 ring-brand/10">
              <div className="flex justify-between text-sm text-slate-600">
                <span>Subtotal</span>
                <span className="tabular-nums font-semibold">{subtotal.toFixed(2)} AED</span>
              </div>
              <div className="flex justify-between text-sm text-slate-600">
                <span>
                  Discount {discountPercent > 0 ? `(${discountPercent}%)` : ""}
                </span>
                <span className={cn("tabular-nums font-semibold", discountAmt > 0 ? "text-red-600" : "")}>
                  {discountAmt > 0 ? `−${discountAmt.toFixed(2)}` : "0.00"} AED
                </span>
              </div>
              <div className="flex justify-between text-sm text-slate-600">
                <span>Tax</span>
                <span className="tabular-nums font-semibold">{tax.toFixed(2)} AED</span>
              </div>
              <div className="border-t border-brand/15 pt-3">
                <div className="flex items-end justify-between gap-3">
                  <span className="text-sm font-bold uppercase tracking-wide text-brand-dark">Total</span>
                  <span className="text-2xl font-extrabold tabular-nums text-brand-dark">
                    {totalPrice.toFixed(2)} <span className="text-base font-bold text-slate-500">AED</span>
                  </span>
                </div>
              </div>
            </div>

            <p className="text-center text-xs text-slate-400">
              By placing this order you confirm the schedule, items, and totals above.
            </p>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default LaundryApp;
